import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';

import { addOneSMSToStore, loadSMSToStore } from 'store/actions/sms';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { truncateText } from 'app/shared/utils/general';
import { Badge } from 'app/shared/badge';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import { useSMSService } from 'hooks/messaging/sms';
import { processDuplicateName } from 'app/shared/utils/mailing';
import { SMSmessageForm } from '../form';
import { checkSMSLength } from '../form/helper';
import { Spacer } from 'app/layouts/generic';
import { setPageTitle } from 'store/actions/header';
import { useSenderIDService } from 'hooks/messaging/sender-id';

const sender_id_message = () => (
  <div>
    <br />
    <p>Before you can send SMS messages, you need to request and get approval for a Sender ID. </p>
    <br />
    <p>What happens next:</p>
    <ul>
      <li>Submit a Sender ID request (takes 2 minutes)</li>
      <li>We'll review and submit to telecom providers</li>
      <li>Approval typically takes 3-5 business days</li>
      <li>You'll be notified once approved</li>
    </ul>
    <br />
    <p>You can request multiple Sender IDs if needed.</p>
  </div>
);

const message_type_tabs = [
  { value: 'campaign', label: 'Campaigns' },
  { value: 'transactional', label: 'Transactional' }
];

export const SMSListPage = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const messages_in_store = useSelector((state) => state.sms);

  const { fetchSenderIds } = useSenderIDService();
  const { createSMS, fetchSMSs, searchSMS, updateSMS } = useSMSService();

  const [has_sender_id, setHasSenderId] = useState(false);
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const [is_search_mode, setSearchMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message_to_edit, setMessageToEdit] = useState({});
  const [message_to_cancel, setMessageToCancel] = useState({});
  const [show_cancel_confirmation, setShowCancelConfirmation] = useState(false);
  const [show_sender_id_warning, setShowSenderIdWarning] = useState(false);
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [total_messages, setTotalMessages] = useState(0);
  const [message_type, setMessageType] = useState('campaign');

  useEffect(() => {
    const parsed_campaigns = Object.values(messages_in_store).sort(
      (a, b) => b.time_stamp - a.time_stamp
    );
    setMessages(() => parsed_campaigns);
  }, [messages_in_store]);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'SMS', path: '.' }]));

    fetchSenderIds().then(({ sender_ids: [sender_id] }) => {
      setHasSenderId(!!sender_id);
    });
  }, []);

  const config = {
    actions: {
      single: (message) => [
        [
          {
            value: 'view',
            label: 'View'
          },
          {
            value: 'edit',
            label: 'Edit'
          },
          { label: 'Clone', value: 'clone' }
        ],
        [
          {
            color: 'red',
            label: 'Cancel',
            value: 'cancel',
            hidden: message.status !== 'queued'
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Title',
        key: 'title',
        isTitle: true,
        formatter: (value) => truncateText(value, 25) || ''
      },
      {
        title: 'Sender ID',
        key: 'sender_id',
        isTagline: true,
        formatter: (value) => value || 'N/A'
      },
      {
        title: 'Status',
        key: 'status',
        formatter: (value) => (
          <Badge
            type={value === 'queued' ? 'orange' : value === 'processed' ? 'green' : 'red'}
            text={value}
          />
        ),
        isBadge: true
      },
      {
        title: 'Type',
        key: 'type',
        formatter: (value) => (
          <Badge type={value === 'campaign' ? 'blue' : 'orange'} text={value} />
        ),
        isBadge: true
      },
      {
        title: 'Send date',
        key: 'schedule',
        formatter: (value) => {
          if (!value || !value?.date) return 'N/A';
          return new Date(value.date).toDateString();
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: messages.sort((a, b) => b.time_stamp - a.time_stamp),
    num_of_rows: 10,
    search_key: 'title,message,sender_id',
    search_text: ''
  };

  const cancelQueued = async (item_data) => {
    if (!item_data.schedule || !item_data.schedule.date) return;

    const now = Date.now();
    const schedule_date = Date.parse(item_data.schedule.date);

    if (schedule_date < now || schedule_date - now < 5 * 60 * 1000) {
      toast.info('Cannot cancel message');
      return;
    }

    toast.info('Canceling Message...');
    const result = await updateSMS(item_data.id, { data: { status: 'draft' } });
    if (result) dispatch(addOneSMSToStore({ ...item_data, status: 'draft' }));
  };

  const duplicate = async (message_data) => {
    const { segments } = checkSMSLength(message_data.message);
    const data = {
      audiences: message_data.audiences,
      is_dnd: message_data.is_dnd || false,
      message: message_data.message,
      pages: segments,
      schedule: message_data.schedule,
      sender_id: message_data.sender_id,
      status: 'draft',
      title: processDuplicateName(message_data.title, 'copy', '-'),
      type: message_data.type
    };

    toast.info('Cloning Message...');
    const { message } = await createSMS({ data });

    if (message) {
      dispatch(addOneSMSToStore(message));
      toast.success('Cloning successful.');
    }
  };

  const handleCancelConfirmation = (permitted) => {
    if (permitted) {
      cancelQueued(message_to_cancel);
    }

    setMessageToCancel({});
    setShowCancelConfirmation(false);
  };

  const handleCreateMessage = () => {
    if (!has_sender_id) {
      setShowSenderIdWarning(true);
      return;
    }

    setMessageToEdit({});
    setShowCreationModal(true);
  };

  const handleRedirect = (can_proceed) => {
    setShowSenderIdWarning(false);
    if (!can_proceed) return;
    history('/sender-ids');
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'edit':
          setMessageToEdit(data);
          setShowCreationModal(true);
          break;
        case 'clone':
          duplicate(data);
          break;
        case 'cancel':
          setMessageToCancel(data);
          setShowCancelConfirmation(true);
          break;
        default:
          history(`/sms/${data.id}`);
      }
    }
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/sms/${id}`);
  };

  const handleDataRequest = async (page, population = 10) => {
    try {
      setLoading(true);
      const { messages, size } = await fetchSMSs({
        query_string: `type=${message_type}&page=${page}&population=${population}&sort_by=-created_on`
      });

      dispatch(loadSMSToStore(messages));
      setTotalMessages(size);
    } catch (e) {
      dispatch(loadSMSToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { messages, size } = await searchSMS(keys, keyword, {
        query_string: `type=${message_type}&page=${page}&population=${population}&sort_by=-created_on`
      });

      setTotalMessages(size);
      dispatch(loadSMSToStore(messages));
    } catch (e) {
      dispatch(loadSMSToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleMessageTypeChange = (type) => {
    dispatch(loadSMSToStore([]));
    setMessageType(type);
  };

  const message_type_toggle = (
    <SegmentedTabs
      options={message_type_tabs}
      value={message_type}
      onChange={handleMessageTypeChange}
      label="Message type"
    />
  );

  const table_actions = (
    <>
      <Button icon_name="add" text="New Message" onClick={handleCreateMessage} />
    </>
  );

  return (
    <>
      <GridRow>
        <GridColumn span={4}>
          {is_mobile_view ? (
            <>
              {message_type_toggle}
              <Spacer multiple={2} />
              <MobileDatatable
                key={message_type}
                config={config}
                action={handleDatatableAction}
                onClick={handleItemClick}
                onListModeChange={setSearchMode}
                onDataRequest={handleDataRequest}
                onSearchRequest={handleSearchRequest}
                showHeader
              />
            </>
          ) : (
            <WebDatatable
              header_tabs={message_type_toggle}
              reset_key={message_type}
              config={{
                ...config,
                total_count: total_messages
              }}
              action={handleDatatableAction}
              onClick={handleItemClick}
              checkbox
              loading_data={loading}
              table_actions={table_actions}
              onDataRequest={handleDataRequest}
              onSearchRequest={handleSearchRequest}
            />
          )}
          <ConfirmationDialog
            type="error"
            title="Cancel Message"
            message="Are you sure you want to cancel this message?"
            callback={handleCancelConfirmation}
            is_open={show_cancel_confirmation}
          />
          <ConfirmationDialog
            action_text="Request Sender ID"
            title="Sender ID Required for SMS"
            message={sender_id_message()}
            callback={handleRedirect}
            is_open={show_sender_id_warning}
          />
          <GmModal
            position="right"
            show_modal={show_creation_modal}
            show_title
            title="Create message"
            onClose={() => setShowCreationModal(false)}
          >
            <SMSmessageForm message_data={message_to_edit} />
          </GmModal>
        </GridColumn>
      </GridRow>
    </>
  );
};
